import React from "react";
import {
  Page,
  Text,
  View,
  Document,
  StyleSheet,
  Svg,
  Path,
  Font,
  G,
} from "@react-pdf/renderer";
import { Order, Spectra, UserData } from "../typings";

Font.register({
  family: "Open Sans",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-regular.ttf",
    },
    {
      src: "https://cdn.jsdelivr.net/npm/open-sans-all@0.1.3/fonts/open-sans-700.ttf",
      fontWeight: 700,
    },
  ],
});

Font.registerHyphenationCallback((word) => {
  // help to break long words
  if (word.length > 35) {
    const regex = new RegExp(`.{1,${35}}`, "g");
    const parts = word.match(regex);
    if (parts !== null) return parts;
  }

  return [word];
});

type Props = {
  spectras: Spectra[];
  order: Order;
  userData: UserData;
};

// Create styles
const styles = StyleSheet.create({
  page: {
    width: "100vw",
    height: "100vh",
    fontFamily: "Open Sans",
    margin: "auto",
    paddingTop: 20,
    paddingBottom: 40,
  },
  header: {
    display: "flex",
    flexDirection: "row",
    fontSize: 30,
    marginBottom: 20,
  },
  logo: {
    marginLeft: "auto",
    //right: "50"
  },
  orderInfos: {
    fontSize: 10,
    marginBottom: 10,
  },
  // helper
  bold: {
    fontWeight: "bold",
  },
  fRow: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    width: "100%",
  },
  fCol: {
    display: "flex",
    flexDirection: "column",
    flexBasis: "100%",
    flex: 1,
  },

  // table
  table: {
    //marginRight: "1cm",
    width: "100%",
    objectFit: "contain",
  },
  row: {
    flexWrap: "wrap",
    display: "flex",
    flexDirection: "row",
    borderTop: "1px solid #000000",
    paddingTop: 8,
    paddingBottom: 8,
    fontSize: 9,
    //flex: 1,
    //height: '100%'
  },
  tableHeader: {
    borderTop: "none",
  },
  // So Declarative and unDRY 👌
  row1: {
    width: "25%",
    flexShrink: 1,
    paddingRight: 10,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  row2: {
    width: "25%",
    paddingRight: 5,
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  row3: {
    width: "5%",
  },
  row4: {
    width: "20%",
    paddingRight: 3,
  },
  row6: {
    width: "20%",
    paddingRight: 3,
  },
  row7: {
    width: "5%",
    paddingRight: 3,
  },
  pageNumber: {
    position: "absolute",
    fontSize: 8,
    bottom: 10,
    left: 0,
    right: 0,
    textAlign: "center",
    color: "#000000",
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
});

export const SpectraQualityReport: React.FC<Props> = ({
  order,
  userData,
  spectras,
}) => (
  <Document>
    <Page size="A4" orientation="landscape" style={styles.page}>
      <View style={{ margin: "0 20" }}>
        <View style={styles.header}>
          <Text fixed>Spectra Quality Report</Text>
          <Svg style={styles.logo} width="132" height="58" viewBox="0 0 132 58">
            <G fill="none" fill-rule="evenodd">
              <G fill="#323328" fill-rule="nonzero">
                <Path d="M15.352 23v-8.672c0-2.336 1.504-4.256 3.84-4.256s3.488 1.856 3.488 4.192V23h3.328v-8.736c0-4.544-2.336-7.296-6.688-7.296-2.144 0-4.128.8-5.408 3.136-.896-2.336-2.944-3.168-4.96-3.168-1.664 0-3.52.64-4.416 2.368l-.224-2.112H1.24V23h3.296v-8.672c0-2.336 1.472-4.288 3.808-4.288s3.68 1.952 3.68 4.288V23h3.328zM41.84 9.72c-.992-1.792-3.136-2.848-5.376-2.848-4.544 0-8.16 2.944-8.16 8.224 0 5.376 3.488 8.352 8.096 8.32 1.952 0 4.384-.96 5.44-2.976L42 23h3.136V7.224h-3.2L41.84 9.72zm-5.12.16c6.816 0 6.816 10.464 0 10.464-2.816 0-5.12-1.984-5.12-5.248 0-3.232 2.304-5.216 5.12-5.216zM57.16 9.976c2.752 0 4.992 1.888 4.992 5.12 0 3.168-2.24 5.152-4.992 5.152-2.784 0-5.056-2.048-5.056-5.152 0-3.04 2.304-5.12 5.056-5.12zM48.616.632V23h3.104l.192-2.496c1.248 2.016 3.36 2.816 5.472 2.816 4.576 0 8.064-3.008 8.064-8.224 0-5.376-3.456-8.224-8-8.224-1.92 0-4.512.96-5.536 2.816V.632h-3.296zM67.968 7.224V23h3.296v-8.544c0-2.912 1.984-4.32 4.256-4.32 1.216 0 1.984.32 2.784.96l1.504-2.88c-.896-.8-2.368-1.344-3.904-1.344-1.696 0-3.488.416-4.64 2.432l-.224-2.08h-3.072zM84.344 7.16V23h-3.296V7.16h3.296zm-3.68-4.576c0-2.688 4.096-2.688 4.096 0 0 2.72-4.096 2.72-4.096 0zM89.584 3.032v4.224h-3.04v2.848h3.04v7.52c0 3.712 1.952 5.664 5.376 5.568 1.12 0 2.08-.224 3.168-.704l-.96-2.816c-.64.32-1.376.544-2.016.544-1.536 0-2.272-.928-2.272-2.592v-7.52h4.64V7.256h-4.608V2.68l-3.328.352zM115.112 16.344c.608-6.432-2.848-9.536-7.936-9.536-4.928 0-8.288 3.424-8.288 8.224 0 5.056 3.328 8.352 8.512 8.352 2.432 0 5.152-.896 6.784-2.688l-2.144-2.08c-.96 1.088-2.976 1.696-4.576 1.696-2.88 0-4.864-1.568-5.152-3.968h12.8zm-12.736-2.784c.512-2.496 2.496-3.808 4.896-3.808 2.656 0 4.48 1.312 4.672 3.808h-9.568zM128.576 18.744c-1.024 1.056-2.4 1.472-3.744 1.472-2.72 0-5.024-1.76-5.024-5.088 0-3.136 2.208-5.152 5.056-5.152 1.248 0 2.56.448 3.552 1.408l2.112-2.208c-1.696-1.664-3.52-2.336-5.696-2.336-4.576 0-8.32 2.848-8.32 8.288s3.776 8.288 8.32 8.288c2.272 0 4.224-.736 5.984-2.464l-2.24-2.208z"></Path>
              </G>
              <G fill="#282C32" fill-rule="nonzero">
                <Path d="M8.36 57.26c1.94 0 3.74-.68 5.04-1.98l-.72-.86c-1.22 1.14-2.7 1.68-4.3 1.68-3.56 0-6.02-2.5-6.02-6.12 0-3.6 2.44-6.08 6-6.08 1.6 0 3.1.6 4.26 1.72l.72-.86c-1.28-1.28-3.04-2.02-4.98-2.02-4.24 0-7.22 3-7.22 7.28 0 4.24 2.98 7.24 7.22 7.24zM23.68 55.86v-5.38h7.56v-1.12h-7.56v-5.24h7.96V43H22.5v14h9.36v-1.14zM41.26 57h1.18V46.04c0-.5 0-.84-.02-1.14l.04-.02c.16.26.36.54 1 1.42L51.28 57h1.18V43h-1.18v10.96c0 .5.02.84.04 1.14l-.04.02c-.18-.26-.38-.54-1.02-1.42L42.48 43h-1.22v14zM72.04 43H60.9v1.1h4.98V57h1.18V44.1h4.98zM89.6 57h1.54l-4.8-5.24c2.62-.16 4.3-1.9 4.3-4.4 0-2.62-1.84-4.36-4.62-4.36H80.5v14h1.2v-5.24h3.12L89.6 57zm-.16-9.6c0 1.98-1.32 3.24-3.44 3.24h-4.3v-6.5h4.28c2.14 0 3.46 1.26 3.46 3.26zM111.08 57h1.3l-6.28-14h-1.36l-6.28 14h1.3l1.52-3.4h8.28l1.52 3.4zm-5.68-12.82h.04c.4 1 .82 1.94 1.62 3.76l2 4.56h-7.3l1.98-4.52c.82-1.8 1.26-2.8 1.66-3.8zM122.2 55.88V43h-1.18v14h9.04v-1.12z"></Path>
              </G>
              <Path stroke="#D3DF00" stroke-width="3" d="M131.5 33.5H.5"></Path>
            </G>
          </Svg>
        </View>

        <View style={[styles.orderInfos, styles.fRow]}>
          <View style={styles.fCol}>
            <Text style={[{ marginBottom: 5 }, styles.bold]}>
              {userData.company}, {userData.firstName} {userData.lastName},{" "}
              {userData.address &&
                `${userData.address.street1} ${userData.address.zip}, ${userData.address.city}, ${userData.address.country}`}
            </Text>
            <Text>
              Customer reference:{" "}
              <Text style={styles.bold}>{order.customerReference}</Text>
            </Text>
            <Text>
              Order ID: <Text style={styles.bold}>{order.orderId}</Text>
            </Text>
            <Text>
              Order date:{" "}
              <Text style={styles.bold}>
                {new Date(order.uploadedAt.seconds * 1000).toLocaleString(
                  "de-ch",
                  { timeZone: "Europe/Berlin" }
                )}
              </Text>
            </Text>
            <Text>
              Number of spectra uploaded:{" "}
              <Text style={styles.bold}>{order.spectraCount}</Text>
            </Text>
            <Text>
              Database used: <Text style={styles.bold}>{order.database}</Text>
            </Text>
          </View>
        </View>
        <View style={styles.table}>
          <View style={[styles.row, styles.bold, styles.tableHeader]}>
            <Text style={styles.row1}>File</Text>
            <Text style={styles.row2}>Sample</Text>
            <Text style={styles.row3}>Score</Text>
            <Text style={styles.row4}>Identification</Text>
            <Text style={styles.row6}>Spectra quality</Text>
            <Text style={styles.row7}>PPM</Text>
          </View>
          {spectras.map((spectra, i) => (
            <View key={i} style={styles.row} wrap={false}>
              <Text style={styles.row1}>{spectra.file}</Text>
              <Text style={styles.row2}>{spectra.sample}</Text>
              <Text style={styles.row3}>
                {spectra.score && spectra.score > 0 ? (
                  <Text style={styles.bold}>{spectra.score}%</Text>
                ) : (
                  "-"
                )}
              </Text>
              <Text style={styles.row4}>
                {spectra.result &&
                  spectra.result.map((s) => (
                    <Text style={styles.bold} key={i}>
                      {s}
                      {"\n"}
                    </Text>
                  ))}
                {spectra.nonQualifiedResult &&
                  spectra.nonQualifiedResult.map((s) => (
                    <Text
                      style={[styles.bold, { backgroundColor: "#dc3545" }]}
                      key={i}
                    >
                      {s}
                      {"\n"}
                    </Text>
                  ))}
                {!spectra.result && !spectra.nonQualifiedResult && (
                  <Text style={{ backgroundColor: "#dc3545" }}>-</Text>
                )}
              </Text>
              <Text style={styles.row6}>
                {spectra.quality ? (
                  <Text style={styles.bold}>{spectra.quality}</Text>
                ) : (
                  "-"
                )}
              </Text>
              <Text style={styles.row7}>
                {spectra.meanPPMError ? (
                    <Text style={styles.bold}>{spectra.meanPPMError}</Text>
                ) : (
                    "-"
                )}
              </Text>
            </View>
          ))}
        </View>
      </View>
      <View style={styles.pageNumber} fixed>
        <Text>
          Order ID: <Text style={styles.bold}>{order.orderId}</Text>
        </Text>
        <Text>Mabritec AG, Lörracherstrasse 50, 4125 Switzerland</Text>
        <Text
          render={({ pageNumber, totalPages }) =>
            `${pageNumber} / ${totalPages}`
          }
          fixed
        />
      </View>
    </Page>
  </Document>
);
